import React from "react";
import styled, { css } from "styled-components";
import i18n from "i18next";
import { ReactReduxContext } from "react-redux";

import GridRow from "components/grid/GridRow";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import ContextMenu from "components/contextmenu/ContextMenu";
import EditableLabel from "components/inputs/EditableLabel";
import expandableIndicatorIcon from "assets/images/expandable_indicator.svg";
import expandableIndicatorIconDark from "assets/images/expandable_indicator_dark.svg";
import menuIcon from "assets/images/menu_downarrow.svg";
import menuIconDark from "assets/images/menu_downarrow_dark.svg";
import ErrorIcon from "assets/images/link_account_warning.svg?react";
import {
  GridRowData,
  GridCellData,
  CurrencyCellData,
  PercentageCellData,
  cellType,
  getCollapsedRowsForRecapSection,
  shouldInsetLeadingCell
} from "components/grid/GridDataModel";
import { getGridCellContentOffsetLeft } from "components/grid/GridCellExports";
import GridHeaderRow from "components/grid/GridHeaderRow";
import GridFooterRow from "components/grid/GridFooterRow";
import {
  getPercentageChange,
  custodiansWithSameParentIdSelector,
  isCryptoCurrency,
  currentPortfolioSelector,
  getTickerUsingId,
  getExchangeRate,
  memoryStorage
} from "@kubera/common";
import CurrencyLabel from "components/labels/CurrencyLabel";
import PercentageLabel from "components/labels/PercentageLabel";
import { contextMenuItemType } from "components/contextmenu/ContextMenu";
import ConfirmationDialog from "components/dialog/ConfirmationDialog";
import getPrefixForDataCy from "components/grid/GridHelpers";
import GridRowsInViewPortWrapper from "components/in_viewport/GridRowsInViewPortWrapper";

const draggedTitleStyles = css`
  display: flex;
  min-height: 50px;
  align-items: center;
  background: ${props => props.theme.sectionMoveTitleWrapperBG};
  margin-bottom: 10px;
  border: 1px solid #d7d7d7;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.25);
  padding-left: 23px;
`;

const ExpandedContainer = styled.div`
  .expanded-title-container-isdragging {
    ${draggedTitleStyles}
  }

  .expanded-title-container {
    display: flex;
    align-items: center;
    padding-bottom: 3px;

    &[data-rbd-drag-handle-context-id] {
      cursor: pointer;
    }
  }
`;

const ExpandIcon = styled.div`
  width: 9px;
  height: 19px;
  margin-right: 3px;
  background-color: transparent;
  background-image: url("${props =>
    props.theme.mode === "default" ? expandableIndicatorIcon : expandableIndicatorIconDark}");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 9px 9px;
  transform: rotate(270deg);
`;

const ErrorWrapper = styled.div`
  margin-right: -10px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ErrorIconComponent = styled(ErrorIcon)`
  path {
    fill: ${props => props.theme.errorIconComponentCLR};
  }
`;

const Title = styled(EditableLabel)`
  color: ${props => props.theme.gridSectionTitleColor};
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  font-feature-settings: "ss01" on;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: transparent;
`;

const SectionContextMenuButton = styled.div`
  position: relative;
  top: 1px;
  width: 19px;
  height: 19px;
  border-radius: 2px;
  margin-bottom: 1px;
  margin-left: 2px;
  cursor: pointer;
  color: red;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 0px 0px;
  background-image: url("${props => (props.theme.mode === "default" ? menuIcon : menuIconDark)}");

  .expanded-title-container:hover & {
    background-size: 9px 9px;
  }

  &:hover {
    background-color: ${props => props.theme.contextMenuButtonBackgroundColor};
  }

  background-size: ${props => (props.isMenuVisible ? "9px 9px" : "")};
  background-color: ${props => (props.isMenuVisible ? props.theme.contextMenuButtonBackgroundColor : "transparent")};
`;

const EntryList = styled.div`
  ${props => {
    const sectionStyles = props.sectionStyles;
    if (sectionStyles && sectionStyles.isSeparateStyle) {
      return css`
        ${sectionStyles}
      `;
    }
    return css`
      flex-direction: column;
      background-color: ${props => props.theme.gridEntryListBackgroundColor};
      border: 1px solid ${props => props.theme.gridSectionBorderColor};
      border-bottom: 0;
      margin-top: -3px;
      ${sectionStyles}

      @media print {
        border: 1px solid rgba(0, 0, 0, 0.1) !important;
        background-color: #ffffff !important;
      }
    `;
  }}
`;

const Entries = styled.div`
  min-height: 40px;
`;

const BlankSectionRowsWrapper = styled.div`
  position: relative;
`;

const BlankSectionAddAssetButton = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 2px dashed transparent;
  box-sizing: border-box;
  cursor: pointer;
  font-style: normal;
  font-weight: 800;
  font-size: 11px;
  line-height: normal;
  text-align: center;
  text-transform: uppercase;
  color: transparent;
  white-space: nowrap;
  z-index: 100;

  ${props =>
    !props.$isDraggingOver &&
    css`
      &:hover {
        background: rgba(252, 252, 252, 0.5);
        border-color: rgba(0, 0, 0, 0.2);
        color: rgba(0, 0, 0, 0.5);
      }
    `}
`;

const CollapsedContainer = styled.div`
  .collapsed-title-container-isdragging {
    ${draggedTitleStyles}
  }

  .collapsed-title-container {
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${props => props.theme.gridSectionCollapsedDividerColor};

    &[data-rbd-drag-handle-context-id] {
      cursor: pointer;
    }
  }
`;

const CollapseIcon = styled(ExpandIcon)`
  transform: rotate(0deg);
`;

const CollapsedTitleContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
`;

const CollapsedSummaryContainer = styled.div`
  display: flex;
`;

const CollapsedPercentageLabel = styled(PercentageLabel)`
  justify-content: center;
  flex-direction: column;
  text-align: right;
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  text-align: right;
  font-feature-settings: "ss01" on;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-right: 8px;
  margin-left: 8px;
  position: relative;
  top: -2px;
  min-width: 50px;
  height: 16px;
  color: ${props => (props.value === 0 ? props.theme.gridCellColor : "")};
`;

const CollapsedCurrencyText = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  text-align: right;
  font-feature-settings: "ss01" on;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-right: 5px;
  margin-left: ${props => `${props.marginLeft}px`};
`;

const CollapsedCurrencyLabel = styled(CurrencyLabel)`
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  text-align: right;
  font-feature-settings: "ss01" on;
  text-overflow: ellipsis;
  overflow: hidden;
  width: ${props => `${props.width}px`};
`;

const AddHistoryBtnWrapper = styled.div`
  padding: 17px 0 17px 10px;
  border-top: 1px solid ${props => props.theme.gridSectionBorderColor};
  background: ${props => props.theme.addHistoryBtnWrapperBG};
`;

const AddHistoryBtn = styled.span`
  font-size: 12px;
  line-height: 15px;
  text-decoration-line: underline;
  color: rgba(0, 0, 0, 0.6);
  cursor: pointer;
`;

const ShowPreviousEntries = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  padding-left: ${getGridCellContentOffsetLeft};
  border-top: 1px solid ${props => props.theme.gridSectionBorderColor};
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  text-decoration-line: underline;
  font-feature-settings: "ss01" on, "calt" off;
  cursor: pointer;
  background: ${props => props.theme.gridSectionHeaderBackgroundColor};
`;

class GridSection extends React.Component {
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);
    this.handleSectionTitleUpdate = this.handleSectionTitleUpdate.bind(this);
    this.handleContextMenuSelection = this.handleContextMenuSelection.bind(this);
    this.handleAddNewButtonClick = this.handleAddNewButtonClick.bind(this);
    this.handleTitleClick = this.handleTitleClick.bind(this);
    this.handleMenuButtonClick = this.handleMenuButtonClick.bind(this);
    this.handleContextMenuDismiss = this.handleContextMenuDismiss.bind(this);
    this.handleRemoveSectionDialogNegativeButtonClick = this.handleRemoveSectionDialogNegativeButtonClick.bind(this);
    this.handleRemoveSectionDialogPositiveButtonClick = this.handleRemoveSectionDialogPositiveButtonClick.bind(this);
    this.handleShowPreviousEntriesClick = this.handleShowPreviousEntriesClick.bind(this);
    this.handleRecapExpandOrCollapse = this.handleRecapExpandOrCollapse.bind(this);

    this.state = {
      isShowingContextMenu: false,
      showRemoveSectionDialog: false,
      removeDialogCount: 0,
      isDragDisabled: !(document.readyState === "complete")
    };

    this.titleRef = React.createRef();
    this.contextMenuRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("click", this.handleOutsideClick, false);

    window.addEventListener("load", () => {
      this.setState({
        isDragDisabled: false
      });
    });
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleOutsideClick, false);
  }

  handleMenuButtonClick(e) {
    if (this.contextMenuRef.current.isVisible() === true) {
      this.contextMenuRef.current.dismiss();
      return;
    }
    const targetPosition = e.target.getBoundingClientRect();
    const menuItems = this.props.section.getContextMenuItems(this.props.section);

    for (const menuItem of menuItems.flat()) {
      if (menuItem.id === contextMenuItemType.REMOVE_SECTION.id) {
        menuItem.disabled = this.props.totalSections < 2;
      }
    }

    this.contextMenuRef.current.show(
      menuItems,
      targetPosition.left,
      targetPosition.top + targetPosition.height + 2,
      true,
      e.target
    );
    this.setState({ ...this.state, isShowingContextMenu: true });
  }

  handleTitleClick(e) {
    if (e.target.id === "section_menu_button") {
      return;
    }
    if (this.titleRef.current !== null && this.titleRef.current.isEditable() === true) {
      return;
    }
    const section = this.props.section;
    section.isCollapsed = !section.isCollapsed;
    this.props.onSectionUpdate(this.props.sheetIndex, this.props.sectionIndex, section);
  }

  getSectionNameForSheetsAndSectionsCollapsedState(section, rowIndex) {
    const selectedRow = section.rows[rowIndex];
    const selectedRowValue = selectedRow.cells[2].value;
    if (selectedRow.category === "sheetHeader") {
      return `${selectedRow.categoryKey}.${selectedRow.sheetId}`;
    } else if (selectedRow.category === "sectionHeader") {
      return `${selectedRow.categoryKey}.${selectedRow.sheetId}.${selectedRow.sectionId}`;
    } else if (selectedRow.category === "categoryHeader") {
      return selectedRowValue;
    }
  }

  handleRecapExpandOrCollapse(rowIndex, isSheetsAndSectionsReport, selectedChartOption, isExpandedView) {
    const section = this.props.section;
    const sectionName = !isSheetsAndSectionsReport
      ? section.name
      : this.getSectionNameForSheetsAndSectionsCollapsedState(section, rowIndex);
    const collapsedState = isSheetsAndSectionsReport ? !section.rows[rowIndex].isCollapsed : !section.isCollapsed;
    section.isCollapsed = isSheetsAndSectionsReport ? false : !section.isCollapsed;
    section.rowIndexClickedOnRecapReports = rowIndex;
    if (isSheetsAndSectionsReport) {
      section.rows = getCollapsedRowsForRecapSection(
        this.props.section.rows,
        this.props.section.isSheetsAndSectionsReport,
        this.props.section.rowIndexClickedOnRecapReports
      );
    }
    this.props.onSectionUpdate(this.props.sheetIndex, this.props.sectionIndex, section);
    const storage = isExpandedView ? memoryStorage : localStorage;
    const recapReportPreferences = storage.getItem("recap_report_preferences");
    const portfolioId = currentPortfolioSelector(this.context.store.getState()).id;
    let updatedReportPreferences = {};
    if (!recapReportPreferences) {
      let recapReportPreferencesForPortfolio = {};
      recapReportPreferencesForPortfolio[selectedChartOption] = {
        ...recapReportPreferencesForPortfolio[selectedChartOption],
        [sectionName]: { isCollapsed: collapsedState }
      };
      updatedReportPreferences[portfolioId] = { ...recapReportPreferencesForPortfolio };
    } else {
      updatedReportPreferences = { ...JSON.parse(recapReportPreferences) };
      if (updatedReportPreferences[portfolioId] !== undefined) {
        updatedReportPreferences[portfolioId] = {
          ...updatedReportPreferences[portfolioId],
          [selectedChartOption]: {
            ...updatedReportPreferences[portfolioId][selectedChartOption],
            [sectionName]: { isCollapsed: collapsedState }
          }
        };
      } else {
        updatedReportPreferences[portfolioId] = {
          [selectedChartOption]: {
            [sectionName]: { isCollapsed: collapsedState }
          }
        };
      }
    }
    storage.setItem("recap_report_preferences", JSON.stringify(updatedReportPreferences));
  }

  handleSectionSort = (key, order) => {
    const section = this.props.section;
    section.columnSortKey = key;
    section.columnSortOrder = order;
    this.props.onSectionUpdate(this.props.sheetIndex, this.props.sectionIndex, section);
  };

  handleContextMenuDismiss() {
    this.setState({ ...this.state, isShowingContextMenu: false });
  }

  handleContextMenuSelection(item) {
    if (item.id === contextMenuItemType.SECTION_ADD_ROW.id) {
      this.props.onAddNewRow(
        this.props.sheetIndex,
        this.props.sectionIndex,
        this.props.section.rows.length,
        undefined,
        true,
        true
      );
    } else if (item.id === contextMenuItemType.SECTION_ADD_SECTION.id) {
      this.props.onAddNewSection(this.props.sheetIndex, this.props.sectionIndex + 1);
    } else if (item.id === contextMenuItemType.RENAME.id) {
      this.renameTitle();
    } else if (item.id === contextMenuItemType.REMOVE_SECTION.id) {
      const rowsAffectedBySection = this.getTotalRowsToBeDeletedWithSection();
      if (rowsAffectedBySection.isRowLinked === true || rowsAffectedBySection.count > 1) {
        this.setState({ ...this.state, showRemoveSectionDialog: true, removeDialogCount: rowsAffectedBySection.count });
      } else {
        this.props.onRemoveSection(this.props.sheetIndex, this.props.sectionIndex);
      }
    }
    if (this.props.onSectionContextMenuSelection) {
      this.props.onSectionContextMenuSelection(this.props.sheetIndex, this.props.sectionIndex, item);
    }
  }

  handleSectionTitleUpdate(value) {
    const section = this.props.section;
    section.name = value;
    this.props.onSectionUpdate(this.props.sheetIndex, this.props.sectionIndex, section);
  }

  renameTitle() {
    if (this.titleRef.current !== null) {
      this.titleRef.current.edit();
    }
  }

  handleAddNewButtonClick(e) {
    const targetPosition = e.target.getBoundingClientRect();
    const menuItems = this.props.section.getFooterContextMenuItems();

    if (Array.isArray(menuItems) && menuItems.length > 0) {
      this.contextMenuRef.current.show(
        menuItems,
        targetPosition.left,
        targetPosition.top + targetPosition.height - 10,
        true,
        e.target
      );
    }

    if (this.props.onAddNewFooterButtonClick) {
      this.props.onAddNewFooterButtonClick(this.props.sheetIndex, this.props.sectionIndex);
    }
  }

  getHeaderRow() {
    var firstRow = this.props.section.rows[0];
    var cells = [];
    this.props.section.columns.forEach((column, index) => {
      if (!firstRow) {
        return;
      }
      if (column.hide === true) {
        return null;
      }

      const firstRowCell = firstRow.cells[index];
      const underlyingCellType = firstRow.cells[index].type;
      const type = column.isButton === true ? underlyingCellType : cellType.HEADER;
      const cell = new GridCellData(type, column.name, null);
      cell.padding = firstRowCell.padding;
      if (column.width) {
        cell.width = column.width;
      }
      if (column.hideTitleIfEmpty === true) {
        if (this.props.section.isColumnEmpty(this.props.sheetIndex, this.props.sectionIndex, index)) {
          cell.name = "";
        }
      }
      if (column.headerCellCss) {
        cell.headerCellCss = column.headerCellCss;
      }
      cell.hasChangeIndicator = column.hasChangeIndicator === true;
      if (column.isRecommendationColumn) {
        cell.isRecommendationCell = true;
        cell.recommendationCount = column.recommendationCount;
      }
      cell.textAlignment = firstRow.cells[index].textAlignment;
      cells.push(cell);
    });

    return new GridRowData(
      null,
      null,
      "header-row-id-" + Math.random(),
      cells,
      firstRow ? firstRow.cellIndexToStretch : 0,
      false,
      () => {
        return true;
      }
    );
  }

  getFooterRow() {
    var firstRow = this.props.section.rows[0];
    var cells = [];

    this.props.section.columns.forEach((column, index) => {
      if (!firstRow) {
        return;
      }
      var cell;
      const columnType = firstRow.cells[index].type;
      const footerAccessoryView = column.getFooterAccessoryView(this.props.sheetIndex, this.props.sectionIndex, index);

      if (index === 0) {
        const menuItems = this.props.section.getFooterContextMenuItems();
        if (!Array.isArray(menuItems) && this.props.showAddNew === true && this.props.isEditable === true) {
          cell = new GridCellData(cellType.ADDNEW_MODAL, null, null);
        } else if (this.props.showAddNew === true && this.props.isEditable === true) {
          cell = new GridCellData(cellType.ADDNEW_BUTTON, null, null);
        } else {
          cell = new GridCellData(cellType.BLANKSPACE, null, null);
        }
      } else if (columnType === cellType.CURRENCY && column.hideTotalForColumn !== true) {
        const total = this.props.section.getTotalForColumn(this.context.store.getState(), index, this.props.currency);
        cell = new CurrencyCellData(
          cellType.FOOTER_CURRENCY,
          column.name,
          this.props.section.isEmpty() === true ? undefined : total,
          this.props.currency
        );
      } else if (columnType === cellType.PERCENTAGE) {
        const firstCellIndex = firstRow.cells[index].firstCellIndex;
        const secondCellIndex = firstRow.cells[index].secondCellIndex;
        var shouldShowPercentage = true;
        var percentage = null;

        if (firstCellIndex !== undefined && secondCellIndex !== undefined) {
          const firstCellTotal = this.props.section.getTotalForColumn(
            this.context.store.getState(),
            firstCellIndex,
            this.props.currency
          );
          const secondCellTotal = this.props.section.getTotalForColumn(
            this.context.store.getState(),
            secondCellIndex,
            this.props.currency
          );
          percentage = getPercentageChange(firstCellTotal, secondCellTotal);

          for (const row of this.props.section.rows) {
            const costCell = row.cells[this.props.section.columnIndexForCost];
            if (row.isComplete() === true && (costCell.value === null || costCell.value === undefined)) {
              shouldShowPercentage = false;
              break;
            }
          }
        } else {
          shouldShowPercentage = false;
        }

        if (percentage !== null && shouldShowPercentage === true) {
          cell = new PercentageCellData(
            cellType.FOOTER_PERCENTAGE,
            column.name,
            percentage,
            firstRow.cells[index].firstCellIndex,
            firstRow.cells[index].secondCellIndex
          );
        } else {
          cell = new GridCellData(cellType.BLANKSPACE, null, null);
          cell.getAccessoryView = () => {
            return footerAccessoryView;
          };
        }
      } else if (columnType === cellType.NUMBER) {
        cell = new GridCellData(cellType.BLANKSPACE, null, null);
      } else if (column.isButton === true) {
        cell = new GridCellData(firstRow.cells[index].type, null, null);
      } else {
        cell = new GridCellData(cellType.BLANKSPACE, column.name, null);
      }
      cells.push(cell);
    });

    return new GridRowData(
      null,
      null,
      "footer-row-id-" + Math.random(),
      cells,
      firstRow ? firstRow.cellIndexToStretch : 0,
      false,
      () => {
        return true;
      }
    );
  }

  get firstVisibleRowIndex() {
    return !this.props.section.hideRowsBeforeRowId === true
      ? 0
      : this.props.section.rows.findIndex(item => item.id === this.props.section.hideRowsBeforeRowId);
  }

  get hasLeadingInset() {
    const rows = this.props.section.rows;
    if (this.leadingInsetForRows !== rows) {
      this.leadingInsetForRows = rows;
      this.leadingInset = shouldInsetLeadingCell(rows);
    }
    return this.leadingInset;
  }

  get categorySectionRows() {
    const { rowsRendered, visibleElementsInArray } = this.props;

    return this.props.section.rows.map((entry, index) => {
      const isWithinVisibleArea =
        rowsRendered + index + 1 <= visibleElementsInArray.current || this.props.section.disableRowWindowing === true;

      return (
        <GridRow
          gridId={this.props.gridId}
          sectionId={this.props.section.id}
          key={entry.id}
          portfolio={this.props.portfolio}
          entry={entry}
          isEditable={this.props.isEditable}
          isVisible={index >= this.firstVisibleRowIndex}
          columns={this.props.section.columns}
          hasLeadingInset={this.hasLeadingInset}
          columnIndexForValue={this.props.section.columnIndexForValue}
          columnIndexForCost={this.props.section.columnIndexForCost}
          currency={this.props.currency}
          entryIndex={index}
          sheetIndex={this.props.sheetIndex}
          sectionIndex={this.props.sectionIndex}
          isLastRow={index === this.props.section.rows.length - 1}
          lastEditableCellIndex={this.props.section.getLastSelectableColumnIndex(true)}
          onMoveToNextCell={this.props.onMoveToNextCell}
          onPaste={this.props.onPaste}
          onRowUpdate={this.handleRowUpdate}
          onAddNewRow={this.props.onAddNewRow}
          onRemoveRow={this.props.onRemoveRow}
          onDetailsClick={this.props.onDetailsClick}
          onOptionsClick={this.props.onOptionsClick}
          onLinkErrorClick={this.props.onLinkErrorClick}
          onUserLeavingCellEmpty={this.props.onUserLeavingCellEmpty}
          onRowContextMenuSelection={this.props.onRowContextMenuSelection}
          onCellBlur={this.props.onCellBlur}
          onCellInvalidTickerAdded={this.props.onCellInvalidTickerAdded}
          onRowClick={this.props.onRowClick}
          userPreferences={this.props.userPreferences}
          gridOptions={this.props.gridOptions}
          isPercentageChangeShown={this.props.isPercentageChangeShown}
          noOfColumns={this.props.section.columns.length - 1}
          onOpenChartsModalClick={this.props.onOpenChartsModalClick}
          isLastSection={this.props.isLastSection}
          handleRecapExpandOrCollapse={this.handleRecapExpandOrCollapse}
          category={this.props.category}
          sectionName={this.props.section.name}
          isDragEnabled={!this.state.isDragDisabled && entry.isDragEnabled}
          isWithinVisibleArea={isWithinVisibleArea}
        />
      );
    });
  }

  handleRemoveSectionDialogNegativeButtonClick(e) {
    this.setState({ ...this.state, showRemoveSectionDialog: false });
  }

  handleRemoveSectionDialogPositiveButtonClick(e) {
    this.props.onRemoveSection(this.props.sheetIndex, this.props.sectionIndex);
  }

  handleAddHistory = () => {
    this.props.onAddNewRow(this.props.sheetIndex, this.props.sectionIndex, this.props.section.getFirstEmptyRowIndex());
  };

  handleShowPreviousEntriesClick(e, firstVisibleIndex) {
    this.props.section.onShowPreviousClick(e, firstVisibleIndex);
  }

  getTotalRowsToBeDeletedWithSection = () => {
    const rows = this.props.section.rows;
    const rowsWithoutParent = rows.filter(row => row.isEmpty() === false && !row.parentId === true);
    var uniqueParentIds = new Set();
    for (const row of rows) {
      if (!row.parentId === false) {
        uniqueParentIds.add(row.parentId);
      }
    }

    var count = rowsWithoutParent.length;
    var isRowLinked = false;
    uniqueParentIds.forEach(parentId => {
      count += custodiansWithSameParentIdSelector(this.context.store.getState(), this.props.portfolio.id, parentId)
        .length;
    });
    if (count === 1 && rowsWithoutParent.length > 0 && !rowsWithoutParent[0].linkType === false) {
      isRowLinked = true;
    }
    return { count, isRowLinked };
  };

  handleRowUpdate = (sheetIndex, sectionIndex, rowIndex, updatedRow, isFirstEdit, shouldUpdateRecapRawData = false) => {
    this.props.onRowUpdate(sheetIndex, sectionIndex, rowIndex, updatedRow, isFirstEdit, shouldUpdateRecapRawData);
  };

  get irrCash() {
    const irrDetails = this.irrDetails;
    if (!irrDetails === true) {
      return null;
    }

    const cashTicker = getTickerUsingId(this.context.store.getState(), irrDetails.cashTickerId);
    const cashExchangeRate = getExchangeRate(
      this.context.store.getState(),
      cashTicker.shortName,
      this.props.portfolio.currency
    );
    const totalCashIn = irrDetails.cashIn * cashExchangeRate;
    const totalCashOut = irrDetails.cashOut * cashExchangeRate;

    return {
      totalCashIn,
      totalCashOut
    };
  }

  get irrDetails() {
    const irrDetails =
      this.props.section.rows.length === 1
        ? this.props.section.rows[0].getIrrDetails()
        : this.props.section.getIrrDetails();
    if (!irrDetails === true) {
      return null;
    }
    return irrDetails[this.props.section.irrTimePeriodKey];
  }

  render() {
    const isCollapsed = this.props.section.isCollapsed;
    const currency = this.props.currency;
    const showTitle = this.props.section.isRecapSection ? false : this.props.showTitle;
    const title = this.props.section.name;
    const headerRow = this.getHeaderRow();
    const footerRow = this.getFooterRow();
    const isSingleSection = this.props.totalSections === 1;
    const datacyPrefix = getPrefixForDataCy();
    const idSuffix = this.props.idSuffix || "";
    const sectionStyles =
      this.props.gridOptions !== undefined &&
      this.props.gridOptions.getSectionStyle !== undefined &&
      this.props.gridOptions.getSectionStyle(
        this.props.section,
        this.props.isLastSection,
        this.props.totalSections,
        this.props.sectionIndex
      );
    window.renderlog("Render: GridSection");
    if (isCollapsed) {
      if (this.props.section.isRecapSection) {
        const collapsedRows = getCollapsedRowsForRecapSection(
          this.props.section.rows,
          this.props.section.isSheetsAndSectionsReport,
          this.props.section.rowIndexClickedOnRecapReports
        );
        return (
          <Entries>
            {collapsedRows.map((entry, index) => {
              return (
                <GridRow
                  gridId={this.props.gridId}
                  sectionId={this.props.section.id}
                  key={entry.id}
                  portfolio={this.props.portfolio}
                  entry={entry}
                  isEditable={this.props.isEditable}
                  isVisible={true}
                  columns={this.props.section.columns}
                  columnIndexForValue={this.props.section.columnIndexForValue}
                  columnIndexForCost={this.props.section.columnIndexForCost}
                  currency={this.props.currency}
                  entryIndex={index}
                  sheetIndex={this.props.sheetIndex}
                  sectionIndex={this.props.sectionIndex}
                  isLastRow={index === this.props.section.rows.length - 1}
                  lastEditableCellIndex={this.props.section.getLastSelectableColumnIndex(true)}
                  onMoveToNextCell={this.props.onMoveToNextCell}
                  onPaste={this.props.onPaste}
                  onRowUpdate={this.handleRowUpdate}
                  onAddNewRow={this.props.onAddNewRow}
                  onRemoveRow={this.props.onRemoveRow}
                  onDetailsClick={this.props.onDetailsClick}
                  onOptionsClick={this.props.onOptionsClick}
                  onLinkErrorClick={this.props.onLinkErrorClick}
                  onUserLeavingCellEmpty={this.props.onUserLeavingCellEmpty}
                  onRowContextMenuSelection={this.props.onRowContextMenuSelection}
                  onCellBlur={this.props.onCellBlur}
                  onCellInvalidTickerAdded={this.props.onCellInvalidTickerAdded}
                  onRowClick={this.props.onRowClick}
                  userPreferences={this.props.userPreferences}
                  gridOptions={this.props.gridOptions}
                  isPercentageChangeShown={this.props.isPercentageChangeShown}
                  noOfColumns={this.props.section.columns.length - 1}
                  onOpenChartsModalClick={this.props.onOpenChartsModalClick}
                  isLastSection={this.props.isLastSection}
                  handleRecapExpandOrCollapse={this.handleRecapExpandOrCollapse}
                  isCollapsed={isCollapsed}
                  sectionName={this.props.section.name}
                  isDragEnabled={entry.isDragEnabled}
                />
              );
            })}
          </Entries>
        );
      } else {
        const totalValue = this.props.section.getTotalValue(this.context.store.getState(), currency);
        const shouldShowError = this.props.section.shouldShowError();
        const isCostAndValueSame = this.props.section.columnIndexForCost === this.props.section.columnIndexForValue;

        return (
          <CollapsedContainer>
            <Draggable
              draggableId={`${this.props.section.id}${idSuffix}`}
              index={this.props.sectionIndex}
              isDragDisabled={
                this.state.isDragDisabled ||
                this.props.isEditable === false ||
                this.props.section.isDragDisabled === true
              }
            >
              {(provided, snapshot) => (
                <div
                  id={this.props.section.id}
                  {...provided.draggableProps}
                  ref={provided.innerRef}
                  {...provided.dragHandleProps}
                  className={snapshot.isDragging ? "collapsed-title-container-isdragging" : "collapsed-title-container"}
                  onClick={this.handleTitleClick}
                >
                  {!this.props.section.onRenderTitle === true ? (
                    <>
                      <CollapsedTitleContainer>
                        {!snapshot.isDragging && (
                          <ExpandIcon
                            data-cy={
                              datacyPrefix +
                              "SectionExpandIconH" +
                              this.props.sheetIndex +
                              "S" +
                              this.props.sectionIndex
                            }
                          />
                        )}
                        <Title
                          type="text"
                          data-cy={
                            datacyPrefix + "SectionTitleH" + this.props.sheetIndex + "S" + this.props.sectionIndex
                          }
                          isCollapsible={isSingleSection === false}
                          isClickable={isSingleSection === false}
                          value={title}
                          onChange={this.handleSectionTitleUpdate}
                        />
                      </CollapsedTitleContainer>

                      {!snapshot.isDragging && (
                        <CollapsedSummaryContainer>
                          {shouldShowError && (
                            <ErrorWrapper>
                              <ErrorIconComponent />
                            </ErrorWrapper>
                          )}
                          {isCostAndValueSame === false && this.irrDetails && (
                            <>
                              {!!this.irrCash.totalCashIn && (
                                <>
                                  <CollapsedCurrencyText marginLeft={20}>
                                    {this.props.section.showCost ? i18n.t("cost") : i18n.t("in")}
                                  </CollapsedCurrencyText>
                                  <CollapsedCurrencyLabel
                                    value={this.irrCash.totalCashIn}
                                    currency={currency}
                                    roundDown={!isCryptoCurrency(this.context.store.getState(), currency)}
                                    maxLongFormatValue={999_999}
                                  />
                                </>
                              )}
                              {!!this.irrCash.totalCashOut && (
                                <>
                                  <CollapsedCurrencyText marginLeft={8}>{i18n.t("out")}</CollapsedCurrencyText>
                                  <CollapsedCurrencyLabel
                                    value={this.irrCash.totalCashOut}
                                    currency={currency}
                                    roundDown={!isCryptoCurrency(this.context.store.getState(), currency)}
                                    maxLongFormatValue={999_999}
                                  />
                                </>
                              )}
                              <CollapsedPercentageLabel
                                showZero={true}
                                value={
                                  this.irrDetails.value !== 0 && this.irrDetails.value > -1 && this.irrDetails.value < 1
                                    ? parseFloat(this.irrDetails.value.toFixed(2))
                                    : Math.kuberaFloor(this.irrDetails.value)
                                }
                              />
                            </>
                          )}
                          {totalValue !== null && (
                            <CollapsedCurrencyLabel
                              value={
                                isCryptoCurrency(this.context.store.getState(), currency)
                                  ? totalValue
                                  : totalValue > 0 && totalValue < 1
                                  ? totalValue
                                  : Math.kuberaFloor(totalValue)
                              }
                              currency={currency}
                              width={120}
                              maxLongFormatValue={999_999}
                            />
                          )}
                        </CollapsedSummaryContainer>
                      )}
                    </>
                  ) : (
                    this.props.section.onRenderTitle(this.props.sheetIndex, this.props.sectionIndex, this.props.section)
                  )}
                </div>
              )}
            </Draggable>
          </CollapsedContainer>
        );
      }
    } else {
      if (this.props.section.isSheetsAndSectionsReport && this.props.section.isRecapSection) {
        return (
          <Entries>
            {this.props.section.rows.map((entry, index) => {
              if (entry.shouldShow) {
                return (
                  <GridRow
                    gridId={this.props.gridId}
                    sectionId={this.props.section.id}
                    key={entry.id}
                    portfolio={this.props.portfolio}
                    entry={entry}
                    isEditable={this.props.isEditable}
                    isVisible={true}
                    columns={this.props.section.columns}
                    columnIndexForValue={this.props.section.columnIndexForValue}
                    columnIndexForCost={this.props.section.columnIndexForCost}
                    currency={this.props.currency}
                    entryIndex={index}
                    sheetIndex={this.props.sheetIndex}
                    sectionIndex={this.props.sectionIndex}
                    isLastRow={index === this.props.section.rows.length - 1}
                    lastEditableCellIndex={this.props.section.getLastSelectableColumnIndex(true)}
                    onMoveToNextCell={this.props.onMoveToNextCell}
                    onPaste={this.props.onPaste}
                    onRowUpdate={this.handleRowUpdate}
                    onAddNewRow={this.props.onAddNewRow}
                    onRemoveRow={this.props.onRemoveRow}
                    onDetailsClick={this.props.onDetailsClick}
                    onOptionsClick={this.props.onOptionsClick}
                    onLinkErrorClick={this.props.onLinkErrorClick}
                    onUserLeavingCellEmpty={this.props.onUserLeavingCellEmpty}
                    onRowContextMenuSelection={this.props.onRowContextMenuSelection}
                    onCellBlur={this.props.onCellBlur}
                    onCellInvalidTickerAdded={this.props.onCellInvalidTickerAdded}
                    onRowClick={this.props.onRowClick}
                    userPreferences={this.props.userPreferences}
                    gridOptions={this.props.gridOptions}
                    isPercentageChangeShown={this.props.isPercentageChangeShown}
                    noOfColumns={this.props.section.columns.length - 1}
                    onOpenChartsModalClick={this.props.onOpenChartsModalClick}
                    isLastSection={this.props.isLastSection}
                    handleRecapExpandOrCollapse={this.handleRecapExpandOrCollapse}
                    isCollapsed={entry.isCollapsed}
                    sectionName={this.props.section.name}
                    isDragEnabled={entry.isDragEnabled}
                  />
                );
              }
              return null;
            })}
          </Entries>
        );
      } else {
        const showRemoveSectionDialog = this.state.showRemoveSectionDialog;

        return (
          <ExpandedContainer id={this.props.section.id}>
            {showTitle && (
              <Draggable
                draggableId={`${this.props.section.id}${idSuffix}`}
                index={this.props.sectionIndex}
                isDragDisabled={
                  this.state.isDragDisabled ||
                  this.props.isEditable === false ||
                  this.props.section.isDragDisabled === true
                }
              >
                {(provided, snapshot) => (
                  <div
                    className={snapshot.isDragging ? "expanded-title-container-isdragging" : "expanded-title-container"}
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                    {...provided.dragHandleProps}
                    onClick={isSingleSection === true ? null : this.handleTitleClick}
                  >
                    {!this.props.section.onRenderTitle === true ? (
                      <>
                        {!snapshot.isDragging && isSingleSection === false && (
                          <CollapseIcon
                            data-cy={
                              datacyPrefix +
                              "SectionCollapseIconH" +
                              this.props.sheetIndex +
                              "S" +
                              this.props.sectionIndex
                            }
                          />
                        )}
                        <Title
                          type="text"
                          ref={this.titleRef}
                          value={title}
                          isClickable={isSingleSection === false}
                          onChange={this.handleSectionTitleUpdate}
                          data-cy={
                            datacyPrefix + "SectionTitleH" + this.props.sheetIndex + "S" + this.props.sectionIndex
                          }
                        />
                        {this.props.isEditable === true &&
                          this.props.section.getContextMenuItems(this.props.section)?.length > 0 && (
                            <SectionContextMenuButton
                              id={"section_menu_button"}
                              data-cy={
                                datacyPrefix +
                                "SectionContextMenuButtonH" +
                                this.props.sheetIndex +
                                "S" +
                                this.props.sectionIndex
                              }
                              onClick={this.handleMenuButtonClick}
                              isMenuVisible={this.state.isShowingContextMenu}
                            />
                          )}
                      </>
                    ) : (
                      this.props.section.onRenderTitle(
                        this.props.sheetIndex,
                        this.props.sectionIndex,
                        this.props.section
                      )
                    )}
                  </div>
                )}
              </Draggable>
            )}
            <EntryList sectionStyles={sectionStyles}>
              <Droppable droppableId={this.props.section.id} type="SECTIONROWS">
                {(provided, snapshot) => (
                  <Entries ref={provided.innerRef} {...provided.droppableProps}>
                    <BlankSectionRowsWrapper>
                      {this.props.section.showHeader === true && (
                        <GridHeaderRow
                          modifyHeaderCss={this.props.section.modifyHeaderCss || ""}
                          sectionId={this.props.section.id}
                          row={headerRow}
                          hasLeadingInset={this.hasLeadingInset}
                          portfolio={this.props.portfolio}
                          userPreferences={this.props.userPreferences}
                          sectionRows={this.props.section.rows}
                          columnSortKey={this.props.section.columnSortKey}
                          columnSortOrder={this.props.section.columnSortOrder}
                          updateSortPreference={this.handleSectionSort}
                          recommendationCount={this.props.recommendationCount}
                        />
                      )}
                      {!this.props.section.hideRowsBeforeRowId === false && (
                        <ShowPreviousEntries
                          key={this.props.section.id + "-show-previous-entries"}
                          isLeadingCell={this.hasLeadingInset}
                          onClick={e => this.handleShowPreviousEntriesClick(e, this.firstVisibleRowIndex)}
                        >
                          {i18n.t("gridSection.showPrevious")}
                        </ShowPreviousEntries>
                      )}
                      <GridRowsInViewPortWrapper>{this.categorySectionRows}</GridRowsInViewPortWrapper>
                      {provided.placeholder}
                      {this.props.section.shouldShowBlankSectionCTA(this.props.isEditable) && (
                        <BlankSectionAddAssetButton
                          data-cy={
                            datacyPrefix +
                            "BlankSectionAddAssetH" +
                            this.props.sheetIndex +
                            "S" +
                            this.props.sectionIndex
                          }
                          onClick={this.handleAddNewButtonClick}
                          role="button"
                          tabIndex={0}
                          $isDraggingOver={snapshot.isDraggingOver}
                        >
                          {i18n.t("add")} {this.props.category}
                        </BlankSectionAddAssetButton>
                      )}
                    </BlankSectionRowsWrapper>
                  </Entries>
                )}
              </Droppable>
              {this.props.addRowBtnTxt && (
                <AddHistoryBtnWrapper>
                  <AddHistoryBtn onClick={this.handleAddHistory}>{this.props.addRowBtnTxt}</AddHistoryBtn>
                </AddHistoryBtnWrapper>
              )}
              {this.props.section.showFooter === true && (
                <GridFooterRow
                  columns={this.props.section.columns}
                  hasLeadingInset={this.hasLeadingInset}
                  sheetIndex={this.props.sheetIndex}
                  sectionIndex={this.props.sectionIndex}
                  row={footerRow}
                  onAddNewClick={this.handleAddNewButtonClick}
                  userPreferences={this.props.userPreferences}
                  category={this.props.category}
                />
              )}
            </EntryList>
            <ContextMenu
              ref={this.contextMenuRef}
              width={233}
              onSelection={this.handleContextMenuSelection}
              onDismiss={this.handleContextMenuDismiss}
            />
            {showRemoveSectionDialog === true && (
              <ConfirmationDialog
                title={
                  i18n
                    .t("removeRows")
                    .replace("%s1%", `${this.state.removeDialogCount}`)
                    .replace("rows", this.state.removeDialogCount === 1 ? "row" : "rows") + "?"
                }
                description={i18n.t("removeSectionDialog.description")}
                positiveButtonTitle={i18n
                  .t("removeRows")
                  .replace("%s1%", `${this.state.removeDialogCount}`)
                  .replace("rows", this.state.removeDialogCount === 1 ? "row" : "rows")}
                negativeButtonTitle={i18n.t("cancel")}
                handleNegativeButtonClick={this.handleRemoveSectionDialogNegativeButtonClick}
                handlePositiveButtonClick={this.handleRemoveSectionDialogPositiveButtonClick}
              />
            )}
          </ExpandedContainer>
        );
      }
    }
  }
}

export default GridSection;
